"use client";

import dynamic from "next/dynamic";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import FadeInUp from "@/components/common/ScrollReveal";

// Priority 2 : Lazy-load popup with ssr:false – zero impact on SSR/LCP
// const WinnersPopUp = dynamic(() => import("@/components/home/WinnersPopUp"), {
//   ssr: false,
// });

// ─── CountUp ────────────────────────────────────────────────────────────────
// Priority 3: Pure IntersectionObserver + rAF. No Framer Motion dependency.
function useCountUp(to: number, duration = 2000) {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLElement>(null);
  const started = useRef(false);

  const run = useCallback(() => {
    if (started.current) return;
    started.current = true;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * to));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [to, duration]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          run();
          observer.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [run]);

  return { ref, value };
}

function Counter({ to }: { from?: number; to: number }) {
  const { ref, value } = useCountUp(to);
  return (
    <span
      ref={ref as React.RefObject<HTMLSpanElement>}
      className="text-mob-30 sm:text-tab-30 lg:text-web-40 leading-[120%] font-normal capitalize tracking-[0.69vw] sm:tracking-[0.390vw] lg:tracking-[0.20vw] text-PrimaryLightBlue mb-mob-8 sm:mb-tab-8 lg:mb-web-8"
    >
      {value}+
    </span>
  );
}

function CounterWithoutPlus({ to }: { from?: number; to: number }) {
  const { ref, value } = useCountUp(to);
  return (
    <span
      ref={ref as React.RefObject<HTMLSpanElement>}
      className="text-mob-30 sm:text-tab-30 lg:text-web-40 leading-[120%] font-normal capitalize tracking-[0.69vw] sm:tracking-[0.390vw] lg:tracking-[0.20vw] text-PrimaryLightBlue mb-mob-8 sm:mb-tab-8 lg:mb-web-8"
    >
      {value}
    </span>
  );
}

// ─── Popup controller ────────────────────────────────────────────────────────
// function PopupController() {
//   const [open, setOpen] = useState(false);
//   useEffect(() => {
//     const t = setTimeout(() => setOpen(true), 800);
//     return () => clearTimeout(t);
//   }, []);
//   if (!open) return null;
//   return (
//     <WinnersPopUp
//       isOpen={open}
//       onClose={() => setOpen(false)}
//       imageSrc="/images/WinnersPopupNew.webp"
//       redirectUrl="/blog/e-raffle-draw-results-january-2026"
//       altText="Lucky draw winners – Gemini UAE e-Raffle January 2026"
//     />
//   );
// }

// ─── AboutSection (exported) ─────────────────────────────────────────────────
export default function AboutSection() {
  return (
    <>
      {/* <PopupController /> */}

      <FadeInUp effect="fadeInUp">
        <div className="pt-mob-80 sm:pt-tab-90 lg:pt-[5.72vw] lg:mb-web-90 lg:relative overflow-hidden">
          <div className="max-w-[90.69vw] sm:max-w-[90.80vw] lg:max-w-[89.58vw] mx-auto lg:flex lg:items-start">
            <h1 className="text-PrimaryLightBlue text-mob-31 sm:text-tab-31 leading-[126%] font-normal mb-mob-12 sm:mb-tab-16 text-center lg:hidden">
              The Largest Supplier of <br className="hidden lg:block" />
              Gypsum Boards and Building Materials <br className="hidden lg:block" />
              in The UAE and Across The GCC
            </h1>
            <div className="max-w-[69.76vw] sm:max-w-[60vw] lg:max-w-[unset] lg:min-w-[27.23vw] lg:basis-[27.23vw] mx-auto lg:mx-0 mb-mob-36 sm:mb-tab-36 lg:mb-0">
              <div className="block lg:hidden">
                <Image
                  src="/images/home-about-banner-mob.webp"
                  alt="banner-one"
                  width={600}
                  height={566}
                  className="w-full h-auto object-contain"
                />
              </div>
              <div className="hidden lg:block">
                <Image
                  src="/images/home-about-banner-web.webp"
                  alt="banner-one"
                  width={522}
                  height={735}
                  priority
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
            <div className="lg:basis-[38vw] lg:mt-[8.5vw] lg:ml-[1vw]">
              <div className="mb-mob-46 sm:mb-tab-46 lg:mb-web-73 lg:text-center relative z-10">
                <h1 className="hidden lg:block lg:text-web-42 lg:leading-[120%] text-PrimaryLightBlue lg:mb-web-16">
                  The Largest Supplier of <br className="hidden lg:block" />
                  Gypsum Boards and Building Materials <br className="hidden lg:block" />
                  in The UAE and Across The GCC
                </h1>
                <p className="text-mob-18 sm:text-tab-18 lg:text-web-24 leading-[120%] lg:leading-[120%] tracking-[0.167vw] sm:tracking-[0.093vw] lg:tracking-[0.05vw] text-ParaLight text-center mb-mob-26 sm:mb-tab-26 lg:mb-web-24 sm:max-w-[60%] lg:max-w-[90%]  sm:mx-auto">
                  We stock and supply a wide range of premium building
                  materials, products, and solutions, widely utilized in
                  construction projects of all scales, across the globe.
                </p>
                <Link href="/about-us">
                  <Button variant={"secondaryLinkBtnBlue"}>
                    Know More
                    <div className="w-mob-7 sm:w-tab-7 lg:w-web-7 h-auto">
                      <Image
                        src="/images/chevron-right-blue.svg"
                        alt="Chevron"
                        width={7}
                        height={12}
                        className="!static !h-auto"
                      />
                    </div>
                  </Button>
                </Link>
              </div>
              <ul className="flex justify-between sm:px-[5%] lg:px-[6%]">
                <li className="flex flex-col items-center w-fit">
                  <div className="w-mob-41 sm:w-tab-41 lg:w-web-50 h-auto mb-mob-11 sm:mb-tab-11 lg:mb-web-17">
                    <Image
                      src="/images/building.svg"
                      alt="Branches"
                      width={50}
                      height={50}
                      className="!static"
                    />
                  </div>

                  <Counter from={0} to={30} />

                  <span className="text-[#2E2E2E] text-mob-18 sm:text-tab-18 lg:text-web-22 font-normal leading-[120%] ">
                    Branches
                  </span>
                </li>
                <li className="flex flex-col items-center w-fit">
                  <div className="w-mob-41 sm:w-tab-41 lg:w-web-50 h-auto mb-mob-11 sm:mb-tab-11 lg:mb-web-17">
                    <Image
                      src="/images/discount-tag.svg"
                      alt="Daily Sales"
                      width={50}
                      height={50}
                      className="!static"
                    />
                  </div>
                  <Counter from={0} to={10000} />

                  <span className="text-[#2E2E2E] text-mob-18 sm:text-tab-18 lg:text-web-22 font-normal leading-[120%] ">
                    Daily Sales
                  </span>
                </li>
                <li className="flex flex-col items-center w-fit">
                  <div className="w-mob-41 sm:w-tab-41 lg:w-web-50 h-auto mb-mob-11 sm:mb-tab-11 lg:mb-web-17">
                    <Image
                      src="/images/global.svg"
                      alt="Countries"
                      width={50}
                      height={50}
                      className="!static"
                    />
                  </div>
                  <CounterWithoutPlus from={0} to={3} />

                  <span className="text-[#2E2E2E] text-mob-18 sm:text-tab-18 lg:text-web-22 font-normal leading-[120%] ">
                    Countries
                  </span>
                </li>
              </ul>
            </div>
          </div>
          <div className="relative  lg:absolute lg:right-[-5.4vw] z-[-1] mt-[-31vw] lg:mt-0 lg:bottom-0 mx-auto lg:w-[47.81vw] lg:h-[38.84vw] ">
            <div className="hidden lg:block">
              <Image
                src="/images/country-map-web.webp"
                alt="map"
                fill
                className="object-cover object-center !h-full"
              />
            </div>
            <div className="block lg:hidden">
              <Image
                src="/images/country-map-mob.webp"
                alt="map"
                fill
                className="!static object-cover object-center !h-full"
              />
            </div>
          </div>
        </div>
      </FadeInUp>
    </>
  );
}
